import { Component, OnInit, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { SmartTemplateService } from '../smart-template.service';
import Swal from 'sweetalert2';

export interface SmartTemplateGroup {
    id?: number;
    name: string;
    description?: string;
    category_id?: number;
    is_active: boolean;
    is_public: boolean;
    priority: number;
    tags?: string;
    created_at?: string;
    updated_at?: string;
}

export interface SmartTemplatePart {
    id?: number;
    name: string;
    type: 'header' | 'content' | 'footer' | 'signature' | 'custom';
    content: string;
    position: 'top' | 'middle' | 'bottom';
    is_required: boolean;
    is_repeatable: boolean;
    order_index: number;
    expanded?: boolean;
}

export interface TemplateParameter {
    id?: number;
    name: string;
    type: 'text' | 'number' | 'date' | 'boolean' | 'select';
    default_value?: string;
    options?: string;
    is_required: boolean;
    description?: string;
}

@Component({
    selector: 'app-smart-template-builder',
    templateUrl: './smart-template-builder.component.html',
    styleUrls: ['./smart-template-builder.component.scss']
})
export class SmartTemplateBuilderComponent implements OnInit {
    editMode = false;
    isSaving = false;
    showPreview = false;
    previewHtml = '';

    templateGroup: SmartTemplateGroup = {
        name: '',
        description: '',
        category_id: undefined,
        is_active: true,
        is_public: false,
        priority: 2,
        tags: ''
    };

    templateParts: SmartTemplatePart[] = [];
    templateParameters: TemplateParameter[] = [];
    categories: any[] = [];
    existingParts: any[] = [];

    constructor(
        public dialogRef: MatDialogRef<SmartTemplateBuilderComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private smartTemplateService: SmartTemplateService
    ) {
        if (data && data.templateId) {
            this.editMode = true;
            this.loadTemplate(data.templateId);
        }
    }

    ngOnInit(): void {
        this.loadCategories();
        this.loadExistingParts();
    }

    onClose(): void {
        this.dialogRef.close({});
    }

    /**
     * Load template for editing
     */
    loadTemplate(templateId: number): void {
        this.smartTemplateService.getSmartTemplate(templateId).subscribe(
            (response: any) => {
                if (response.success && response.data) {
                    this.templateGroup = response.data.template;
                    this.templateParts = response.data.parts || [];
                    this.templateParameters = response.data.parameters || [];

                    // Ensure all parts have expanded property
                    this.templateParts.forEach(part => {
                        part.expanded = false;
                    });
                }
            },
            (error) => {
                console.error('Error loading template:', error);
                Swal.fire({
                    title: 'Eroare',
                    text: 'Nu s-a putut încărca template-ul pentru editare.',
                    icon: 'error'
                });
            }
        );
    }

    /**
     * Load categories for template categorization
     */
    loadCategories(): void {
        this.smartTemplateService.getCategories().subscribe(
            (response: any) => {
                if (response.success && response.data) {
                    this.categories = response.data;
                }
            },
            (error) => {
                console.error('Error loading categories:', error);
            }
        );
    }

    /**
     * Load existing template parts for reuse
     */
    loadExistingParts(): void {
        this.smartTemplateService.getAvailableParts().subscribe(
            (response: any) => {
                if (response.success && response.data) {
                    this.existingParts = response.data;
                }
            },
            (error) => {
                console.error('Error loading existing parts:', error);
            }
        );
    }

    /**
     * Validate form data
     */
    isFormValid(): boolean {
        if (!this.templateGroup.name || this.templateGroup.name.trim().length === 0) {
            return false;
        }

        // Check if we have at least one part
        if (this.templateParts.length === 0) {
            return false;
        }

        // Validate all parts have names and content
        for (const part of this.templateParts) {
            if (!part.name || !part.content) {
                return false;
            }
        }

        // Validate parameters
        for (const param of this.templateParameters) {
            if (!param.name || !param.type) {
                return false;
            }
        }

        return true;
    }

    /**
     * Add new template parameter
     */
    addParameter(): void {
        const newParameter: TemplateParameter = {
            name: '',
            type: 'text',
            default_value: '',
            is_required: false,
            description: ''
        };
        this.templateParameters.push(newParameter);
    }

    /**
     * Remove template parameter
     */
    removeParameter(index: number): void {
        this.templateParameters.splice(index, 1);
    }

    /**
     * Add new template part
     */
    addNewPart(): void {
        const newPart: SmartTemplatePart = {
            name: '',
            type: 'content',
            content: '',
            position: 'middle',
            is_required: false,
            is_repeatable: false,
            order_index: this.templateParts.length,
            expanded: true
        };
        this.templateParts.push(newPart);
    }

    /**
     * Add existing part to template
     */
    addExistingPart(): void {
        // This would open a dialog to select from existing parts
        // For now, we'll just add a placeholder
        Swal.fire({
            title: 'Selectează partea existentă',
            text: 'Această funcționalitate va fi implementată pentru a permite selectarea părților existente.',
            icon: 'info'
        });
    }

    /**
     * Remove template part
     */
    removePart(index: number): void {
        Swal.fire({
            title: 'Confirmare ștergere',
            text: 'Sigur doriți să ștergeți această parte?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Șterge',
            cancelButtonText: 'Anulează'
        }).then((result) => {
            if (result.isConfirmed) {
                this.templateParts.splice(index, 1);
                // Reorder remaining parts
                this.templateParts.forEach((part, i) => {
                    part.order_index = i;
                });
            }
        });
    }

    /**
     * Duplicate template part
     */
    duplicatePart(index: number): void {
        const originalPart = this.templateParts[index];
        const duplicatedPart: SmartTemplatePart = {
            ...originalPart,
            id: undefined,
            name: originalPart.name + ' - Copie',
            order_index: this.templateParts.length,
            expanded: false
        };
        this.templateParts.push(duplicatedPart);
    }

    /**
     * Toggle part expansion
     */
    togglePartExpansion(index: number): void {
        this.templateParts[index].expanded = !this.templateParts[index].expanded;
    }

    /**
     * Handle drag and drop reordering
     */
    onPartDrop(event: CdkDragDrop<SmartTemplatePart[]>): void {
        moveItemInArray(this.templateParts, event.previousIndex, event.currentIndex);

        // Update order indices
        this.templateParts.forEach((part, index) => {
            part.order_index = index;
        });
    }

    /**
     * Get part type label
     */
    getPartTypeLabel(type: string): string {
        const labels: { [key: string]: string } = {
            'header': 'Header',
            'content': 'Conținut',
            'footer': 'Footer',
            'signature': 'Semnătură',
            'custom': 'Personalizat'
        };
        return labels[type] || type;
    }

    /**
     * Preview template
     */
    previewTemplate(): void {
        // Generate preview HTML by combining all parts
        let html = '<div class="template-preview">';

        // Sort parts by order and position
        const sortedParts = [...this.templateParts].sort((a, b) => a.order_index - b.order_index);

        sortedParts.forEach(part => {
            html += `<div class="template-part part-${part.type}">`;
            html += `<h4 class="part-title">${part.name}</h4>`;
            html += `<div class="part-content">${part.content}</div>`;
            html += '</div>';
        });

        html += '</div>';

        this.previewHtml = html;
        this.showPreview = true;
    }

    /**
     * Close preview
     */
    closePreview(): void {
        this.showPreview = false;
        this.previewHtml = '';
    }

    /**
     * Export preview as PDF
     */
    exportPreview(): void {
        // This would integrate with PDF generation service
        Swal.fire({
            title: 'Export PDF',
            text: 'Funcționalitatea de export PDF va fi implementată.',
            icon: 'info'
        });
    }

    /**
     * Save template
     */
    saveTemplate(): void {
        if (!this.isFormValid()) {
            Swal.fire({
                title: 'Eroare validare',
                text: 'Vă rugăm să completați toate câmpurile obligatorii.',
                icon: 'error'
            });
            return;
        }

        this.isSaving = true;

        const templateData = {
            template: this.templateGroup,
            parts: this.templateParts,
            parameters: this.templateParameters
        };

        const saveOperation = this.editMode
            ? this.smartTemplateService.updateSmartTemplate(this.templateGroup.id!, templateData)
            : this.smartTemplateService.createSmartTemplate(templateData);

        saveOperation.subscribe(
            (response: any) => {
                this.isSaving = false;
                if (response.success) {
                    Swal.fire({
                        title: 'Succes',
                        text: `Template ${this.editMode ? 'actualizat' : 'creat'} cu succes!`,
                        icon: 'success'
                    }).then(() => {
                        this.dialogRef.close({ success: true, data: response.data });
                    });
                } else {
                    Swal.fire({
                        title: 'Eroare',
                        text: response.message || 'A apărut o eroare la salvarea template-ului.',
                        icon: 'error'
                    });
                }
            },
            (error) => {
                this.isSaving = false;
                console.error('Error saving template:', error);

                let errorMessage = 'A apărut o eroare neașteptată.';
                if (error.error && error.error.message) {
                    errorMessage = error.error.message;
                }

                Swal.fire({
                    title: 'Eroare',
                    text: errorMessage,
                    icon: 'error'
                });
            }
        );
    }

    /**
     * Cancel and close dialog
     */
    cancel(): void {
        if (this.hasUnsavedChanges()) {
            Swal.fire({
                title: 'Modificări nesalvate',
                text: 'Aveți modificări nesalvate. Sigur doriți să ieșiți?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Da, ieși',
                cancelButtonText: 'Rămâi'
            }).then((result) => {
                if (result.isConfirmed) {
                    this.dialogRef.close({ success: false });
                }
            });
        } else {
            this.dialogRef.close({ success: false });
        }
    }

    /**
     * Check if there are unsaved changes
     */
    private hasUnsavedChanges(): boolean {
        return this.templateGroup.name.length > 0 ||
            this.templateParts.length > 0 ||
            this.templateParameters.length > 0;
    }
}